import { ThemeOptions } from '@mui/material';

const defaultTheme: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1650, // all default except xl
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          scrollbarWidth: 'thin',
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          scrollbarWidth: 'thin',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: '0',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          background: 'unset',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: ({ theme }) => ({
          background: 'transparent',
          color: theme.palette.secondary.main,
          border: '1px solid ',
          borderRadius: '0.75em',
          borderColor: theme.palette.secondary.main,
          padding: '0 4px',
          lineHeight: 1.2,
          minHeight: 'unset',
          height: 'auto',
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          if (ownerState.variant === 'outlined') {
            return {
              color: theme.palette.secondary.main,
              border: `1px solid ${theme.palette.secondary.dark}`,
              textDecoration: 'none',
              m: '1rem 1.5rem',
              borderRadius: 9999,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.secondary.dark,
                textDecoration: 'none',
              },
              '& a': {
                color: theme.palette.text.primary,
                textDecoration: 'none',
              },
            };
          }
          return {
            '&:hover': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.secondary.dark,
            },
            color: ownerState.disabled
              ? theme.palette.action.disabled
              : theme.palette.primary.main,
            backgroundColor: ownerState.disabled
              ? theme.palette.action.disabledBackground
              : theme.palette.secondary.main,
            borderColor: ownerState.disabled
              ? theme.palette.action.disabledBackground
              : theme.palette.secondary.main,
            borderRadius: 9999,
          };
        },
      },
      variants: [
        {
          props: { color: 'error', variant: 'contained' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.error.main,
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }),
        },
        {
          props: { color: 'error', variant: 'outlined' },
          style: ({ theme }) => ({
            backgroundColor: 'transparent',
            color: theme.palette.error.main,
            border: `1px solid ${theme.palette.error.main}`,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
            },
          }),
        },
      ],
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.light,
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        thumb: ({ ownerState, theme }) => ({
          backgroundColor: ownerState.checked
            ? theme.palette.secondary.main
            : theme.palette.text.primary,
          // Use the parent .Mui-disabled selector for correct disabled styling
          '.Mui-disabled &': {
            backgroundColor: theme.palette.action.disabled,
          },
        }),
        track: ({ theme }) => ({
          '.Mui-checked.Mui-checked + &': {
            opacity: 0.8,
            backgroundColor: theme.palette.secondary.main,
          },
          '.Mui-disabled + &': {
            backgroundColor: theme.palette.action.disabled,
            opacity: 1,
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
          '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main,
          },
          '&.Mui-selected': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&.Mui-checked': {
            color: theme.palette.secondary.main,
          },
          '&.Mui-disabled': {
            color: theme.palette.action.disabled,
          },
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&.Mui-checked': {
            color: theme.palette.secondary.main,
          },
          '&.Mui-disabled': {
            color: theme.palette.action.disabled,
          },
        }),
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.secondary.main,
          '&:hover': {
            color: theme.palette.secondary.dark,
          },
        }),
        valueLabel: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&:hover': {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: () => ({
          color: 'cyan',
          '&:hover': {
            color: 'cyan',
          },
        }),
      },
    },
  },

  typography: {
    fontFamily: 'Titillium Web, sans-serif',
    button: {
      textTransform: 'none',
    },
    h1: {
      fontWeight: 'bold',
    },
    h2: {
      fontWeight: 'bold',
    },
    h3: {
      fontWeight: 'bold',
    },
    h4: {
      fontWeight: 'bold',
    },
    h5: {
      fontWeight: 'bold',
      fontSize: '22px',
    },
    h6: {
      fontWeight: 'bold',
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#03152d',
      dark: '#01070f',
      light: '#062652',
    },
    secondary: {
      main: '#4bfa91',
      dark: '#1f7040',
      light: '#afedc8',
    },
    background: {
      default: '#49454f',
      paper: '#231f20',
    },
    text: {
      primary: '#fff',
      secondary: '#bbb',
    },
    action: {
      disabled: '#a8b0bf',
      disabledBackground: '#223147',
    },
    warning: {
      main: '#F5A100',
      dark: '#b37500', // Darker shade of main for hover effect
    },
    error: {
      light: '#F54B41',
      main: '#D12721',
      dark: '#8e1a17', // Darker shade of main for hover effect
    },
  },
};

export default defaultTheme;
